import { Box, Divider, Grid, LinearProgress, Paper, styled, Typography } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import moment from "moment";
import { useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import PageHeader from "@/components/PageHeader";
import { useQueryOptionsFactory } from "@/queries";
import type { PaginatedReservations, Reservations } from "@/queries/reservations.ts";
import "react-big-calendar/lib/css/react-big-calendar.css";
import type { Unit } from "@/queries/units.ts";

const CalendarContainer = styled(Box)(() => ({
    "& .rbc-calendar": {
        backgroundColor: "white",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    "& .rbc-header": {
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        padding: "12px 8px",
        fontWeight: 600,
        color: "#1e293b",
    },
    "& .rbc-today": {
        backgroundColor: "#fef3c7",
    },
    "& .rbc-toolbar": {
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderBottom: "1px solid #e2e8f0",
        "& button": {
            backgroundColor: "white",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            padding: "8px 16px",
            margin: "0 4px",
            fontWeight: 500,
            "&:hover": {
                backgroundColor: "#f3f4f6",
            },
            "&.rbc-active": {
                backgroundColor: "#28638c",
                color: "white",
                borderColor: "#28638c",
            },
        },
    },
    "& .rbc-month-view, & .rbc-time-view": {
        border: "none",
    },
    "& .rbc-date-cell": {
        padding: "8px",
        "&.rbc-off-range-bg": {
            backgroundColor: "#f9fafb",
        },
    },
    "& .rbc-event": {
        borderRadius: "4px",
        padding: "2px 6px",
        cursor: "pointer",
        "&:hover": {
            opacity: 0.8,
            transform: "scale(1.02)",
        },
        transition: "all 0.2s ease",
    },
}));
export const Route = createFileRoute("/admin/units/$unitId/calendar")({
    component: RouteComponent,
    pendingComponent: LinearProgress,
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(
            context.qof.reservations.getByUnitId(params.unitId),
        );
    },
});

function RouteComponent() {
    const navigate = useNavigate();

    const { unitId } = Route.useParams();
    const qof = useQueryOptionsFactory();
    const unitData: Unit = useSuspenseQuery(qof.units.get(unitId)).data;
    const reservationData: PaginatedReservations = useSuspenseQuery(
        qof.reservations.getByUnitId(unitId),
    ).data;

    const [selectedReservation, setselectedReservation] = useState<Reservations | null>(null);
    const [calendarDate, setCalendarDate] = useState<Date>(new Date());

    const formatDate = (date: Date) => {
        return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });
    };
    const eventStyleGetter = () => {
        return {
            style: {
                backgroundColor: "#ef4444",
                borderRadius: "4px",
                opacity: 0.9,
                color: "white",
                border: "0px",
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
            },
        };
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title={`Unit Calendar - Unit #${unitData.rentalId}`}
                    instructions={[
                        `Viewing calendar for Unit #${unitData.rentalId} (${reservationData.data[0]?.calendar.owner.ownerFirstName} ${reservationData.data[0]?.calendar.owner.ownerName})`,
                        "Click on a date range to add a new reservation",
                        "Click on an existing reservation to edit or delete it",
                    ]}
                    onBack={() => navigate({ to: "/admin/units" })}
                    backText="← Back to All Units"
                />
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <CalendarContainer sx={{ height: { xs: "500px", md: "600px" } }}>
                        <Calendar
                            localizer={momentLocalizer(moment)}
                            events={reservationData.data}
                            startAccessor="startDate"
                            endAccessor="endDate"
                            titleAccessor={"customerName"}
                            style={{ height: "100%" }}
                            onSelectEvent={(res) => setselectedReservation(res)}
                            views={["month"]}
                            date={calendarDate}
                            onNavigate={setCalendarDate}
                            eventPropGetter={eventStyleGetter}
                            popup
                            showMultiDayTimes
                            step={60}
                            timeslots={1}
                        />
                    </CalendarContainer>

                    {selectedReservation && (
                        <Box sx={{ mt: 3 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 2,
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        backgroundColor: "#22c55e",
                                        borderRadius: 1,
                                    }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                    Reservation
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                                Date: {formatDate(selectedReservation.startDate)}
                            </Typography>
                            <Grid
                                container
                                sx={{
                                    backgroundColor: "#f8fafc",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 1,
                                    p: 2,
                                }}
                            >
                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
                                    >
                                        Reservation
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#1e293b" }}>
                                        {formatDate(selectedReservation.startDate)} -{" "}
                                        {formatDate(selectedReservation.endDate)}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
                                    >
                                        Customer
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#1e293b" }}>
                                        {selectedReservation.customerFirstName}{" "}
                                        {selectedReservation.customerName}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
                                    >
                                        Email
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#1e293b" }}>
                                        {selectedReservation.email || "N/A"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
